import { useState, useCallback } from 'react';
import useTranslation from "../../../../hooks/useTranslation";

import { partitionRows } from '../../../../../../common/model/condition/partition';

import BaseList from "./BaseList"
import allocationFor from '../allocationFor';

/**
 * Which rows are already this line's, and which are still on offer.
 *
 * This was `filterItems` here in the file: a location counted as reserved when
 * it had a `reservedQuantity` and one of its reservations named this document
 * and this line — the line found by splitting a `<line>.<n>` composite that
 * `MultiSelect` wrote and nothing documented. It is `partitions` in the model
 * now, in the app's predicate language, matching an explicit `lineIndex`
 * (33.2 steps 7 and 8).
 *
 * The two names below are the two lists `BaseList` renders — one selectable, one
 * read-only. That pairing stays in code because the component has exactly two;
 * what moved out is the rule for which rows go where.
 */
const partitionsFor = (partitions, testId) => {
    if (Array.isArray(partitions) && partitions.length > 0) return partitions

    // A list that renders nothing and says nothing is worse than one that
    // complains — the same call made for `pickList`'s `dataSource` (33.2 §4b).
    console.warn(`<multiSelect> '${testId}' declares no partitions, so every row is offered`)
    return [{ id: 'available' }]
}


const MultiList = ({
    data = [],
    onSelectionChange,
    sumField,
    constraint,
    columns = ['Location', 'Quantity'],
    existingSelections = [],
    emptyMessage,
    lineId,
    docId,
    listItems,
    testId,
    singleSelect = false,
    partitions,
    identity = 'locationId',
    selectable = true,
    record
}) => {

    // `lineId` is this line's index in the section — the number `MultiSelect`
    // stamps onto each reservation as `lineIndex`. It is named for the context
    // the predicate reads, not for the prop it arrives in.
    const { available: availData = [], reserved: resData = [] } = partitionRows(
        data,
        partitionsFor(partitions, testId),
        // The record is the context a row predicate is judged against, plus the
        // two coordinates the modal itself supplies. That is what lets a
        // partition ask about a sibling array — `none: '$reservations'` — rather
        // than only about the row in front of it.
        { context: { ...record, docId, lineIndex: lineId } }
    );

    const { t } = useTranslation()

    // Which rows are already chosen.
    //
    // This compared `selection.locationId === item.locationId`, here and again in
    // `getItemAllocation` - a field name written into a widget that renders
    // whatever it is given, and one of the two entries 1.8 filed under "should be
    // an idField". `identity` is the `valueMap` key holding the row's id, so the
    // comparison is against `item.id` and the same for every list (33.2 step 10).
    //
    // It defaults to `locationId` so the three location configs keep working
    // without saying it, which is also what the old constant meant.
    let initialSelectedItems = data.filter(item =>
        existingSelections.some(selection =>
            selection[identity] === item.id
        )
    );
    if (singleSelect && initialSelectedItems.length > 1) {
        initialSelectedItems = [initialSelectedItems[0]];
    }


    const [selectedItems, setSelectedItems] = useState(initialSelectedItems);


    // What the rows selected so far have taken. The same per-row arithmetic
    // `MultiSelect` uses to write the quantity, so the ceiling this enforces
    // and the quantity that gets stored cannot disagree.
    const calculateTotalSum = useCallback((items) => {
        return items.reduce(
            (acc, item) => acc + allocationFor(item, sumField, constraint - acc), 0);
    }, [sumField, constraint]);

    const getItemAllocation = (item) => {
        const existingSelection = existingSelections.find(
            selection => selection[identity] === item.id
        );
        return existingSelection ? existingSelection.quantity : 0;
    };

    const handleToggleSelection = (item) => {
        const isSelected = selectedItems.some(selected => selected.id === item.id);
        let newSelectedItems;

        if (singleSelect) {
            // Only allow one selection at a time
            newSelectedItems = isSelected ? [] : [item];
        } else {
            if (isSelected) {
                newSelectedItems = selectedItems.filter(selected => selected.id !== item.id);
            } else {
                newSelectedItems = [...selectedItems, item];
            }
        }

        // Update local state first
        setSelectedItems(newSelectedItems);

        // Use a small timeout to ensure state is updated before notifying parent
        // This prevents race conditions when clicking rapidly
        setTimeout(() => {
            onSelectionChange(newSelectedItems);
        }, 250);
    };

    const isItemSelectable = (item) => {
        // A list that only shows. Both reservation tabs are this: rows with an
        // action underneath, not a choice — and `BaseList` ties clicking a row to
        // selecting it, so saying no here is what makes them display lists.
        if (!selectable) return false;

        // In singleSelect mode always allow toggling (selection or deselection)
        if (singleSelect) {
            return true;
        }

        // If item is already selected, allow deselection
        if (selectedItems.some(selected => selected.id === item.id)) {
            return true;
        }

        // If we've already allocated the full constraint, prevent further selection
        const currentSum = calculateTotalSum(selectedItems);
        if (constraint && currentSum >= constraint) {
            return false;
        }

        return true;
    };

    return (
        <BaseList
            columns={columns}
            emptyMessage={emptyMessage}
            data={availData}
            biData={resData}
            selectedItems={selectedItems}
            isItemSelectable={isItemSelectable}
            handleSelection={handleToggleSelection}
            getTagValue={getItemAllocation}
            testId={listItems ? `${testId}.${listItems}` : testId}
        />
    );
};

export default MultiList;
